import {isPlatformBrowser} from '@angular/common';
import { Injectable, PLATFORM_ID, DOCUMENT, inject } from '@angular/core';
import {BehaviorSubject} from 'rxjs';

export type ThemeMode = 'light' | 'dark' | 'system';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private document = inject<Document>(DOCUMENT);

  private readonly STORAGE_KEY = 'theme';
  private readonly LEGACY_STORAGE_KEY = 'snoball_dark_theme';
  private readonly themeMode = new BehaviorSubject<ThemeMode>('light');
  private readonly darkTheme = new BehaviorSubject<boolean>(false);
  private readonly isBrowser: boolean;
  private mediaQueryList: MediaQueryList | null = null;
  private readonly mediaQuery = '(prefers-color-scheme: dark)';

  readonly themeMode$ = this.themeMode.asObservable();
  readonly isDarkTheme$ = this.darkTheme.asObservable();

  constructor() {
    const platformId = inject(PLATFORM_ID);

    this.isBrowser = isPlatformBrowser(platformId);
  }

  loadTheme(): void {
    if (!this.isBrowser) {
      this.themeMode.next('light');
      this.darkTheme.next(false);
      return;
    }

    this.ensureSystemThemeListener();

    const stored = localStorage.getItem(this.STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') {
      this.applyThemeMode(stored, false);
      return;
    }

    const legacyStored = localStorage.getItem(this.LEGACY_STORAGE_KEY);
    if (legacyStored === 'true') {
      this.applyThemeMode('dark', false);
      return;
    }

    this.applyThemeMode('light', false);
  }

  setThemeMode(mode: ThemeMode): void {
    this.applyThemeMode(mode, true);
  }

  private applyThemeMode(mode: ThemeMode, persist: boolean): void {
    this.themeMode.next(mode);
    this.darkTheme.next(this.resolveIsDark(mode));

    if (!this.isBrowser) {
      return;
    }

    if (persist) {
      localStorage.setItem(this.STORAGE_KEY, mode);
      localStorage.removeItem(this.LEGACY_STORAGE_KEY);
    }

    this.document.body.classList.toggle('dark-theme', this.darkTheme.value);
  }

  private resolveIsDark(mode: ThemeMode): boolean {
    if (mode === 'dark') {
      return true;
    }

    if (mode === 'system' && this.isBrowser) {
      return this.getSystemPrefersDark();
    }

    return false;
  }

  private getSystemPrefersDark(): boolean {
    if (!this.isBrowser) {
      return false;
    }

    if (!this.mediaQueryList) {
      this.mediaQueryList = window.matchMedia(this.mediaQuery);
    }

    return this.mediaQueryList.matches;
  }

  private ensureSystemThemeListener(): void {
    if (!this.isBrowser || this.mediaQueryList) {
      return;
    }

    this.mediaQueryList = window.matchMedia(this.mediaQuery);
    const onSystemThemeChange = () => {
      if (this.themeMode.value === 'system') {
        this.applyThemeMode('system', false);
      }
    };

    if (typeof this.mediaQueryList.addEventListener === 'function') {
      this.mediaQueryList.addEventListener('change', onSystemThemeChange);
    } else {
      this.mediaQueryList.addListener(onSystemThemeChange);
    }
  }
}
